import { useEffect, useCallback, useMemo } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { usePermissions } from "../../../hooks/usePermissions";
import { useNavigate, useLocation } from "react-router-dom";
import {
  faEdit,
  faTrash,
  faCubes,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import ConfirmationModal from "../../../components/modals/ConfirmationModal";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import ListHeader from "../../../components/ListHeader";
import useAction from "../../../hooks/useAction";
import useUrlFilters from "../../../hooks/useUrlFilters";
import useGenericFilter from "../../../hooks/filters/useGenericFilter";
import { transformFiltersForUrl } from "../../../utils/transformFiltersForUrl";
import AutoCompleteFilter from "../../../components/AutoCompleteFilter";
import baseService from "../../../services/baseService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useFilterHandlers from "../../../hooks/useFilterHandlers";
import TableWithInactiveFilter from "../../../components/table/TableWithInactiveFilter";
import FilterCollapse from "../../../components/FilterCollapse";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";

const KitPage = () => {
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { get: baseServiceGet, del: baseServiceDelete } =
    useBaseService(navigate);

  // --- Padronização de filtros via useUrlFilters ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      name: [],
      deleted_at: false,
    },
    "equipments_kits",
  );

  // --- Callbacks para o useGenericFilter ---
  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters);

  // --- Gerenciamento de Filtros Locais do Formulário ---
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        name: [],
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear,
    );

  // --- Lógica de busca de kits com TanStack Query ---
  const {
    data: kitsResponseData,
    isLoading: isLoadingKits,
    isFetching: isFetchingKits,
    isError: isErrorKits,
    error: kitsQueryError,
  } = useQueryWithLoader({
    queryKey: ["equipments_kits", filters, pagination],
    queryFn: async () => {
      showLoader();
      const filtersTranformed = transformFiltersForUrl(filters);
      const params = {
        filters: filtersTranformed,
        page: pagination.page,
        perPage: pagination.perPage,
      };
      const response = await baseServiceGet(
        entities.equipmentsKits.get,
        params,
      );
      return response.result;
    },
    select: (data) => {
      return {
        kits: data.data.map((kit) => ({
          id: kit.id,
          name: kit.name,
          isDeleted: !!kit.deleted_at,
        })),
        totalPages: data.last_page,
      };
    },
    staleTime: 60 * 1000, // 60 segundos de cache
    keepPreviousData: true, // Mantém os dados antigos enquanto busca os novos
  });

  const { kits = [], totalPages = 1 } = kitsResponseData || {};

  const columns = [{ key: "name", label: "Nome" }];

  // --- Gerenciamento de Loader com base nos estados do TanStack Query ---
  useEffect(() => {
    if (isLoadingKits || isFetchingKits) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isLoadingKits, isFetchingKits, showLoader, hideLoader]);

  // --- Notificação de Erro para a Query de Kits ---
  useEffect(() => {
    if (isErrorKits) {
      console.error(
        "KitPage: Erro ao carregar kits (useQuery):",
        kitsQueryError,
      );
      showNotification(
        "error",
        `Erro ao carregar kits: ${
          kitsQueryError.response?.data?.message || kitsQueryError.message
        }`,
      );
    }
  }, [isErrorKits, kitsQueryError, showNotification]);

  // --- Função para Forçar o Refetch da Lista (invalida o cache) ---
  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["equipments_kits", filters, pagination],
    });
  }, [queryClient, filters, pagination]);

  // --- Gerenciamento de Modal de Confirmação e Ações ---
  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate);

  const inactiveKitMutation = useMutation({
    mutationFn: async (kitIdToActivate) => {
      showLoader();
      const response = await baseServiceDelete(
        entities.equipmentsKits.delete(kitIdToActivate),
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 200) refreshList();
    },
    onError: (err) => {
      console.error("onError ", err);
      hideLoader();
    },
    onSettled: () => {
      hideLoader();
      handleCancelConfirmation();
    },
  });

  const activateKitMutation = inactiveKitMutation;

  // --- useEffect para mensagens de estado de localização ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, showNotification, navigate, location.pathname]);

  const actions = useMemo(
    () => [
      {
        id: "edit",
        icon: faEdit,
        title: "Editar",
        buttonClass: "btn-primary",
        permission: "Atualizar kits",
        onClick: (kit) =>
          navigate(`/aluguel/kits-equipamentos/editar/${kit.id}`),
        show: (kit) => !kit.isDeleted && canAccess("Atualizar kits"),
      },
      {
        id: "viewGroups",
        icon: faCubes,
        title: "Ver itens do kit",
        buttonClass: "btn-info",
        permission: "Listar item dos kits",
        onClick: (kit) =>
          navigate(`/aluguel/kits-equipamentos/${kit.id}/itens`),
        show: (kit) => !kit.isDeleted && canAccess("Listar item dos kits"),
      },
      {
        id: "delete",
        icon: faTrash,
        title: "Excluir",
        buttonClass: "btn-danger",
        permission: "Excluir kits",
        onClick: (kit) =>
          showConfirmation(
            kit,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactiveKitMutation.mutateAsync(kit.id);
              } catch (error) {
                console.error("Erro ao excluir kit:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (kit) => !kit.isDeleted && canAccess("Excluir kits"),
      },
      {
        id: "activate",
        icon: faUndo,
        title: "Restaurar",
        buttonClass: "btn-info",
        permission: "Excluir kits",
        onClick: (kit) =>
          showConfirmation(
            kit,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateKitMutation.mutateAsync(kit.id);
              } catch (error) {
                console.error("Erro ao restaurar kit:", {
                  status: error.status,
                  message: error.message,
                });
              }
            },
          ),
        show: (kit) => kit.isDeleted && canAccess("Excluir kits"),
      },
    ],
    [
      navigate,
      canAccess,
      showConfirmation,
      inactiveKitMutation,
      activateKitMutation,
    ],
  );

  // Inputs para o formulário de filtros
  const inputsfilters = useMemo(
    () => [
      {
        label: "Nome",
        gridSize: "12",
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase="name"
            model="equipmentKit"
            value={localFilterValues.name}
            onChange={(selected) => handleFilterChange("name", selected)}
            placeholder="Filtre os kits pelo nome"
            isMulti
            isClearable
          />
        ),
      },
    ],
    [localFilterValues, handleFilterChange],
  );

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Kits" showBackButton={true} backUrl="/dashboard" />
      <div className="container-fluid p-1">
        {/* Formulário de Filtros */}
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        {/* Cabeçalho da Lista de Kits */}
        <ListHeader
          title="Lista de Kits"
          buttonText="Novo Kit"
          buttonLink="/aluguel/kits-equipamentos/criar"
          canAccess={canAccess}
          permission="Criar kits"
        />

        {/* Tabela Dinâmica de Kits */}
        <TableWithInactiveFilter
          data={kits}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value;
            setPagination({ page: 1, perPage: newPerPage });
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoadingKits}
          isFetching={isFetchingKits}
        />

        {/* Modal de Confirmação */}
        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.name : ""}
          text={action.text}
        />
      </div>
    </MainLayout>
  );
};

export default KitPage;
